/**
 * Which section of a match's offer each market sits in.
 *
 * A soccer match carries over a hundred markets; listed one after another they
 * read as a wall nobody finds anything in. The owner's client asked
 * (2026-10-20, screen recording) for them sorted the way betbigwin.de does it:
 * a short "Popular" section with the bets most people play, then sections for
 * handicaps, goal totals, each half, goal events, corners, cards, players,
 * specials and combinations, each opened on demand.
 *
 * Soccer markets are placed by name, from the feed's actual offer (the list
 * below covers every market the feed sent on 2026-10-20). Other sports, and
 * any market the feed adds later, are placed by the words in their name, and
 * whatever matches nothing lands in "Other" rather than disappearing.
 *
 * Grouping only changes where a market is shown; the market itself, its name
 * and its order within the section stay as marketCatalog puts them.
 */

import { layoutKeyOf, LinesPart } from './marketLines';

export type MarketGroup =
  | 'main'
  | 'popular'
  | 'handicap'
  | 'totals'
  | 'firstHalf'
  | 'secondHalf'
  | 'periods'
  | 'quarters'
  | 'sets'
  | 'games'
  | 'goals'
  | 'corners'
  | 'cards'
  | 'players'
  | 'specials'
  | 'combos'
  | 'asian'
  | 'other';

/** Top to bottom, as the sections are listed. */
export const MARKET_GROUPS: MarketGroup[] = [
  'main',
  'popular',
  'handicap',
  'totals',
  'firstHalf',
  'secondHalf',
  'periods',
  'quarters',
  'sets',
  'games',
  'goals',
  'corners',
  'cards',
  'players',
  'specials',
  'combos',
  'asian',
  'other',
];

/** Asian handicaps and goal lines, whole and quarter lines included. */
const ASIAN_MARKETS = [
  'Asian Handicap',
  'Alternative Asian Handicap',
  'Goal Line',
  'Alternative Goal Line',
  '1st Half Asian Handicap',
  'Alternative 1st Half Asian Handicap',
  '1st Half Goal Line',
  'Alternative 1st Half Goal Line',
  '2nd Half Goal Line',
  'Asian Corners',
  'Asian Total Corners',
  'Asian Handicap Corners',
  '1st Half Asian Corners',
  'Asian Total Cards',
  'Asian Handicap Cards',
];

/** Soccer feed names, by section. */
const BY_NAME: Record<
  Exclude<
    MarketGroup,
    'main' | 'other' | 'periods' | 'quarters' | 'sets' | 'games'
  >,
  string[]
> = {
  // What the client listed as "popularno" (betbigwin's first section).
  popular: [
    // The 1 X 2 the app lifts out of the feed's market for the match row
    // (FULL_TIME_BETGROUP_NAME).
    'Full Time',
    'Full Time Result',
    'Match Result',
    'Double Chance',
    'Goals Over/Under',
    'Both Teams to Score',
    'Rest of Match - Both Teams to Score',
    'Teams to Score',
    'Draw No Bet',
    'Half Time/Full Time',
    'Half Time/Full Time & 3+ Goals',
    'Result / Total Goals',
    'Results/Goals Range',
    'Result/Both Teams to Score',
    'Total Goals/Both Teams to Score',
  ],
  handicap: [
    'Handicap Result',
    'Handicap',
    'Alternative Handicap Result',
    'Handicap 2-Way',
    'Match Handicap',
  ],
  totals: [
    'Total Goals',
    'Alternative Total Goals',
    'Exact Total Goals',
    'Number of Goals In Match',
    'Exact Goals',
    'Goals Range',
    'Match Goals Range',
    'Goals Odd/Even',
    'Team Total Goals',
    'Team Goals Range',
    'Home Team Exact Goals',
    'Away Team Exact Goals',
    'Home Team Odd/Even Goals',
    'Away Team Odd/Even Goals',
  ],
  firstHalf: [
    'Half Time Result',
    'Half Time Double Chance',
    '1st Half Handicap Result',
    '1st Half Handicap',
    'Alternative 1st Half Handicap Result',
    '1st Half Handicap 2-Way',
    '1st Half Goals',
    '1st Half Goals Range',
    'Exact 1st Half Goals',
    '1st Half Goals Odd/Even',
    'Both Teams to Score in 1st Half',
    'Half Time Correct Score',
  ],
  secondHalf: [
    '2nd Half Result',
    '2nd Half Goals',
    '2nd Half Goals Range',
    'Exact 2nd Half Goals',
    '2nd Half Goals Odd/Even',
    'Both Teams to Score in 2nd Half',
  ],
  goals: [
    'Correct Score',
    'First Team to Score',
    'Last Team to Score',
    'Clean Sheet',
    'To Score In Half',
    'Both Teams to Score 1st Half - 2nd Half',
    'Half With Most Goals',
    'Home Team Highest Scoring Half',
    'Away Team Highest Scoring Half',
    'Winning Margin',
    'Time of First Goal Brackets',
    'Time of 1st Team Goal',
    'Early Goal',
    'Late Goal',
    'Total Goal Minutes',
    'First 10 Minutes',
    'First 10 Minutes (00:00 - 09:59)',
    '10 Minute Result',
    'First Goal Method',
    'Own Goal',
    'To Score a Penalty',
    'To Miss a Penalty',
    'A Penalty in the Match',
  ],
  corners: [
    'Corners',
    'Alternative Corners',
    'Corners 2-Way',
    'Total Corners',
    'Corner Handicap',
    'Corners Race',
    'Team Corners',
    'Multicorners',
    'First Match Corner',
    'Last Match Corner',
    'Time of First Corner',
    '1st Half Corners',
  ],
  cards: [
    'Number of Cards In Match',
    'Card Handicap',
    'Team Cards',
    'First Card Received',
    'Time of First Card',
    'Both Teams to Receive Cards',
    'Red Card in Match/Half',
    'Either to be Booked',
  ],
  players: [
    'Goalscorers',
    'Team Goalscorer',
    'Multi Scorers',
    'Score or Assist',
    'Either to Score',
    'Either to Score First',
    'Either to Assist',
    'Either to Score or Assist',
    'Goal Method',
    'Goalkeeper Saves',
  ],
  specials: [
    'Specials',
    'Team to Get Most',
    'Match Shots',
    'Match Shots on Target',
    'Team Shots',
    'Team Shots on Target',
    'Match Offsides',
    'Team Offsides',
    'Match Tackles',
    'Team Tackles',
    'Team Performances',
    'BetBoost',
    'Full Time Result – Enhanced Prices',
    'First Minute (00:00 - 00:59)',
    'First Set Piece (00:00 - 04:59)',
  ],
  combos: [
    'Double Chance/Goals Range',
    'Half Time Result/Both Teams To Score',
    'Half Time Result / Total Goals',
    'Result & Goals',
    'Result & Half Goals',
    'Double Chance & 1st Half Goals',
    'Result & Half With More Goals',
    'Double Chance & Half With More Goals',
    'Half Time/Full Time & Goals',
    'Half Time/Full Time Combos',
    'Goals in Each Half',
  ],
  // Asian and quarter lines (2.0, "2.0, 2.5"): kept, but out of the way at
  // the bottom - the client could not tell what they settle on.
  asian: ASIAN_MARKETS,
};

const normalize = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '');

const GROUP_OF_NAME = new Map<string, MarketGroup>();
(Object.keys(BY_NAME) as Array<keyof typeof BY_NAME>).forEach((group) =>
  BY_NAME[group].forEach((name) => GROUP_OF_NAME.set(normalize(name), group))
);

/** Placing by the words in a name, first match wins. */
const BY_WORDS: Array<[RegExp, MarketGroup]> = [
  [/\basian\b/, 'asian'],
  [/corner/, 'corners'],
  [/\bcards?\b|booked|booking/, 'cards'],
  [
    /scorer|player|assist|rebound|threes|goalkeeper|double double|triple double|^points$/,
    'players',
  ],
  [/(1st|first) half|half time/, 'firstHalf'],
  [/(2nd|second) half/, 'secondHalf'],
  [
    /quarter|period|\bsets?\b|\d(st|nd|rd|th) set|10 minute|inning|\bmap\b|\bround\b/,
    'periods',
  ],
  [/(result|handicap|winner).*(\band\b|&|\/).*total|double result/, 'combos'],
  [
    /game lines|money ?line|to win match|match winner|^winner|full time result|match result|3-way$|double chance|draw no bet|both teams to score/,
    'popular',
  ],
  [/handicap|spread|puck line|run line/, 'handicap'],
  [/total|over|under|odd\/even|goal line/, 'totals'],
];

export const marketGroupOf = (name?: string | null): MarketGroup => {
  if (!name) return 'other';
  const named = GROUP_OF_NAME.get(normalize(name));
  if (named) return named;
  const n = name.toLowerCase();
  return BY_WORDS.find(([words]) => words.test(n))?.[1] ?? 'other';
};

/**
 * A sport's offer layout from the admin (GET /api/offer_layout): its
 * sections in order, each with the markets it lists, in order. A market of
 * a lines market's part reads "Game Lines#handicap" (layoutKeyOf).
 */
export type OfferSection = { key: string; markets: string[] };

const isGroup = (key: string): key is MarketGroup =>
  (MARKET_GROUPS as string[]).includes(key);

const isWordChar = (c: string | undefined) => !!c && /[\p{L}\p{N}]/u.test(c);

/** Replaces name where it stands as words of its own ("A" not in "Asian"). */
function replaceWord(s: string, name: string, as: string): string {
  if (!name) return s;
  let out = '';
  for (;;) {
    const i = s.indexOf(name);
    if (i < 0) return out + s;
    const end = i + name.length;
    out +=
      !isWordChar(s[i - 1]) && !isWordChar(s[end])
        ? s.slice(0, i) + as
        : s.slice(0, end);
    s = s.slice(end);
  }
}

/**
 * A live market name as a live layout names it: the teams as {home} and
 * {away}, a score in brackets as (*) - "Sporting Goals" is "{home} Goals".
 * The API reads names the same way (bets.LiveMarketPattern).
 */
export function liveMarketPattern(
  market: string,
  home?: string | null,
  away?: string | null
): string {
  const teams = [
    { name: (home ?? '').trim(), as: '{home}' },
    { name: (away ?? '').trim(), as: '{away}' },
  ].sort((a, b) => b.name.length - a.name.length);
  let m = market.trim();
  for (const t of teams) m = replaceWord(m, t.name, t.as);
  return m.replace(/\(\d+-\d+\)/g, '(*)');
}

type Teams = { home?: string | null; away?: string | null };

const escapeRegExp = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * The markets in the layout's sections and order. A market the layout does
 * not place goes to "Other" at the bottom, in the order it came in, so a
 * market the feed adds later is still offered. With the teams, live names
 * are matched as patterns too ("{home} Goals", "* Goal").
 */
function groupByLayout<T extends { name?: string | null; part?: LinesPart }>(
  bets: T[],
  sections: OfferSection[],
  teams?: Teams
): Array<{ group: MarketGroup; bets: T[] }> {
  const place = new Map<string, { section: number; order: number }>();
  const globs: Array<{ re: RegExp; section: number; order: number }> = [];
  sections.forEach((s, section) =>
    s.markets.forEach((m, order) => {
      const key = m.toLowerCase();
      if (!place.has(key)) place.set(key, { section, order });
      if (key.includes('*')) {
        const re = key.split('*').map(escapeRegExp).join('.*');
        globs.push({ re: new RegExp(`^${re}$`), section, order });
      }
    })
  );
  const placeOf = (bet: T) => {
    const name = String(bet.name ?? '');
    const keys = [layoutKeyOf(bet), name];
    if (teams) keys.push(liveMarketPattern(name, teams.home, teams.away));
    for (const k of keys) {
      const key = k.toLowerCase();
      const exact = place.get(key);
      if (exact) return exact;
      const glob = globs.find((g) => g.re.test(key));
      if (glob) return glob;
    }
    return undefined;
  };
  const placed = sections.map(
    () => [] as Array<{ bet: T; order: number; at: number }>
  );
  const rest: T[] = [];
  bets.forEach((bet, at) => {
    const p = placeOf(bet);
    if (p && isGroup(sections[p.section].key))
      placed[p.section].push({ bet, order: p.order, at });
    else rest.push(bet);
  });
  const out = sections
    .map((s, i) => ({
      group: s.key as MarketGroup,
      // Cards of one market keep the order they came in (lines in order).
      bets: placed[i]
        .sort((a, b) => a.order - b.order || a.at - b.at)
        .map((x) => x.bet),
    }))
    .filter((s) => s.bets.length);
  if (rest.length) {
    const other = out.find((s) => s.group === 'other');
    if (other) other.bets.push(...rest);
    else out.push({ group: 'other', bets: rest });
  }
  return out;
}

/**
 * The markets split into their sections. With the sport's layout from the
 * admin, its sections and order; without one, sections in MARKET_GROUPS
 * order by the market's name (empty ones left out), markets keeping their
 * order within a section.
 */
export function groupMarkets<
  T extends { name?: string | null; part?: LinesPart },
>(
  bets: T[],
  sections?: OfferSection[] | null,
  teams?: Teams
): Array<{ group: MarketGroup; bets: T[] }> {
  // Tennis in play: the games of the set (who wins the game being played and
  // the next ones, its score, deuce, a break) in a section of their own
  // right under the main bet. The layout lists only some of them and the
  // rest sat at the bottom under "Other" (owner, 2026-11-03).
  const games = bets.filter((b) => TENNIS_GAME.test(String(b?.name ?? '')));
  if (games.length) {
    const rest = groupMarkets(
      bets.filter((b) => !games.includes(b)),
      sections,
      teams
    );
    const at = rest[0]?.group === 'main' ? 1 : 0;
    rest.splice(at, 0, {
      group: 'games',
      bets: [...games].sort(
        (a, b) => tennisGameRank(a.name) - tennisGameRank(b.name)
      ),
    });
    return rest;
  }
  if (sections?.length) return groupByLayout(bets, sections, teams);
  const byGroup = new Map<MarketGroup, T[]>();
  bets.forEach((bet) => {
    const group = marketGroupOf(bet?.name);
    byGroup.set(group, [...(byGroup.get(group) ?? []), bet]);
  });
  return MARKET_GROUPS.filter((g) => byGroup.has(g)).map((group) => ({
    group,
    bets: byGroup.get(group)!,
  }));
}

/** A tennis game market: "Set 2 - Game 4 Winner", "Set 2 Game 3 Score",
 *  "Set 2 - Games 4 & 5 - Winner", and the breaks of serve. */
const TENNIS_GAME =
  /^(Set \d+\s*-?\s*Games? \d+\b.*|Next Break of Serve|Set \d+ to Break Serve)$/i;

/** Per game in the order played (the next game first), within a game the
 *  winner first; two-game markets after their first game; breaks last. */
const GAME_KIND = [
  /winner$/i,
  /server to win$/i,
  /score$/i,
  /to have break point$/i,
  /to deuce$/i,
  /total points$/i,
];
function tennisGameRank(name?: string | null): number {
  const n = String(name ?? '');
  const m = /^Set (\d+)\s*-?\s*Games? (\d+)/i.exec(n);
  if (!m) return 1e9 + (/^next/i.test(n) ? 0 : 1);
  const kind = GAME_KIND.findIndex((re) => re.test(n));
  const pair = /&/.test(n) ? 50 : 0;
  return (
    Number(m[1]) * 1e6 +
    Number(m[2]) * 1e3 +
    pair +
    (kind < 0 ? GAME_KIND.length : kind)
  );
}

/** For tests: every soccer feed name placed by name. */
export const GROUPED_FEED_NAMES = Object.values(BY_NAME).flat();

/**
 * The modern layout's sections, as forzza.com (the app that layout copies)
 * names them: Main, Full time, Halftime, one section per team, Special,
 * Cards and corners, Players. Asian lines again sit at the bottom.
 */
export type ModernMarketGroup =
  | 'main'
  | 'fullTime'
  | 'halftime'
  | 'homeTeam'
  | 'awayTeam'
  | 'periods'
  | 'specials'
  | 'cardsCorners'
  | 'players'
  | 'asian'
  | 'other';

export const MODERN_MARKET_GROUPS: ModernMarketGroup[] = [
  'main',
  'fullTime',
  'halftime',
  'homeTeam',
  'awayTeam',
  'periods',
  'specials',
  'cardsCorners',
  'players',
  'asian',
  'other',
];

/** Soccer markets whose forzza section differs from the betbigwin one. */
const MODERN_BY_NAME: Partial<Record<ModernMarketGroup, string[]>> = {
  main: [
    'Full Time',
    'Full Time Result',
    'Match Result',
    'Double Chance',
    'Handicap Result',
    'Handicap',
    'Alternative Handicap Result',
    'Handicap 2-Way',
    'Match Handicap',
    'Goals Over/Under',
    'Alternative Total Goals',
    'Total Goals',
    'Draw No Bet',
    'First Team to Score',
    'Correct Score',
  ],
  fullTime: [
    'Last Team to Score',
    'Both Teams to Score',
    'Rest of Match - Both Teams to Score',
    'Teams to Score',
    'Exact Total Goals',
    'Number of Goals In Match',
    'Exact Goals',
    'Goals Odd/Even',
    'Winning Margin',
    'Result & Goals',
    'Result & Half Goals',
    'Double Chance & 1st Half Goals',
  ],
  halftime: [
    'Half Time/Full Time',
    'Half Time/Full Time & 3+ Goals',
    'Half Time/Full Time & Goals',
    'Half Time/Full Time Combos',
    'Goals in Each Half',
    'Result & Half With More Goals',
    'Double Chance & Half With More Goals',
    'Both Teams to Score 1st Half - 2nd Half',
    'Half With Most Goals',
    'To Score In Half',
    'Exact 1st Half Goals',
    'Exact 2nd Half Goals',
  ],
  homeTeam: [
    'Home Team Exact Goals',
    'Home Team Odd/Even Goals',
    'Home Team Highest Scoring Half',
  ],
  awayTeam: [
    'Away Team Exact Goals',
    'Away Team Odd/Even Goals',
    'Away Team Highest Scoring Half',
  ],
  specials: [
    'Goals Range',
    'Match Goals Range',
    'Result / Total Goals',
    'Results/Goals Range',
    'Result/Both Teams to Score',
    'Total Goals/Both Teams to Score',
  ],
};

const MODERN_OF_NAME = new Map<string, ModernMarketGroup>();
(Object.keys(MODERN_BY_NAME) as ModernMarketGroup[]).forEach((group) =>
  MODERN_BY_NAME[group]!.forEach((name) =>
    MODERN_OF_NAME.set(normalize(name), group)
  )
);

/** Where the rest go: the betbigwin section, merged into forzza's. */
const MODERN_FROM_CLASSIC: Record<MarketGroup, ModernMarketGroup> = {
  main: 'main',
  popular: 'main',
  handicap: 'main',
  totals: 'fullTime',
  firstHalf: 'halftime',
  secondHalf: 'halftime',
  periods: 'periods',
  quarters: 'periods',
  sets: 'periods',
  // Tennis games right after the winner, as in the other layouts.
  games: 'main',
  goals: 'fullTime',
  corners: 'cardsCorners',
  cards: 'cardsCorners',
  players: 'players',
  specials: 'specials',
  combos: 'specials',
  asian: 'asian',
  other: 'other',
};

/**
 * A card's modern section. A card about one team (that team's goals, its
 * clean sheet) goes to that team's section, as forzza lists them.
 */
export const modernMarketGroupOf = (card: {
  name?: string | null;
  team?: 'home' | 'away';
}): ModernMarketGroup => {
  const classic = marketGroupOf(card.name);
  if (classic === 'asian') return 'asian';
  if (card.team && !['corners', 'cards', 'specials'].includes(classic))
    return card.team === 'home' ? 'homeTeam' : 'awayTeam';
  return (
    MODERN_OF_NAME.get(normalize(card.name ?? '')) ??
    MODERN_FROM_CLASSIC[classic]
  );
};
